import React, { CSSProperties, ReactNode, useMemo } from 'react';
import { TimelineStateConsumer } from '../timeline/TimelineStateContext';
import CustomHeader from './CustomHeader';
import { getNextUnit, SelectUnits } from '../utility/calendar';
import { defaultHeaderFormats } from '../default-config';
import memoize from 'memoize-one';
import { CustomDateHeader } from './CustomDateHeader';
import type {
  IntervalRenderer,
  SidebarHeaderChildrenFnProps,
  TimelineTimeSteps,
} from '../types/main';
import type { Dayjs, UnitType } from 'dayjs';

type GetHeaderData<Data> = (
  intervalRenderer: (p: IntervalRenderer<Data>) => ReactNode,
  style: React.CSSProperties,
  className: string | undefined,
  getLabelFormat: (
    interval: [Dayjs, Dayjs],
    unit: keyof typeof defaultHeaderFormats,
    labelWidth: number
  ) => string,
  unitProp: UnitType | 'primaryHeader' | undefined,
  headerData: Data | undefined
) => {
  intervalRenderer?: IntervalRenderer<Data>;
  style: React.CSSProperties;
  className: string;
  getLabelFormat: (
    interval: [Dayjs, Dayjs],
    unit: keyof typeof defaultHeaderFormats,
    labelWidth: number
  ) => string;
  unitProp: UnitType | 'primaryHeader' | undefined;
  headerData: Data;
};

export interface DateHeaderProps<Data> {
  style?: CSSProperties;
  className?: string;
  unit?: keyof TimelineTimeSteps | 'primaryHeader';
  timelineUnit: SelectUnits;
  labelFormat?:
    | string
    | ((interval: [Dayjs, Dayjs], unit: UnitType | 'primaryHeader', labelWidth: number) => string);
  intervalRenderer?: (props: IntervalRenderer<Data>) => ReactNode;
  headerData?: Data;
  children?: (props: SidebarHeaderChildrenFnProps<Data>) => ReactNode;
  height?: number;
}

const getRootStyleMemo = memoize((style: React.CSSProperties | undefined) => ({
  height: 30,
  ...style,
}));

const getHeaderDataMemo = memoize(
  (
    intervalRenderer: ((p: IntervalRenderer<unknown>) => ReactNode) | undefined,
    style: React.CSSProperties,
    className: string | undefined,
    getLabelFormat: (
      interval: [Dayjs, Dayjs],
      unit: keyof typeof defaultHeaderFormats,
      labelWidth: number
    ) => string,
    unitProp: UnitType | 'primaryHeader' | undefined,
    headerData: unknown,
  ) => ({
    intervalRenderer,
    style,
    className: className ?? '',
    getLabelFormat,
    unitProp,
    headerData,
  }),
);

function DateHeaderInner<Data>({
  unit,
  timelineUnit,
  labelFormat,
  style,
  className,
  intervalRenderer,
  headerData,
  height,
}: DateHeaderProps<Data>) {
  const headerUnit: keyof TimelineTimeSteps =
    unit === 'primaryHeader' ? getNextUnit(timelineUnit) : (unit ?? timelineUnit);

  const getLabelFormat = useMemo(
    () =>
      (
        interval: [Dayjs, Dayjs],
        unitKey: keyof typeof defaultHeaderFormats,
        labelWidth: number,
      ) => {
        if (typeof labelFormat === 'string') {
          return interval[0].format(labelFormat);
        }
        if (typeof labelFormat === 'function') {
          return labelFormat(interval, unitKey as UnitType, labelWidth);
        }
        throw new Error('labelFormat should be function or string');
      },
    [labelFormat],
  );

  // Construir headerData siempre (aunque intervalRenderer sea undefined). Cuando es undefined,
  // Interval renderiza el div por defecto con intervalText (año, mes, etc.); si pasáramos una
  // función (p. ej. () => null) Interval usaría ese Renderer y no pintaría nada.
  const headerDataValue = useMemo(
    () =>
      getHeaderDataMemo(
        intervalRenderer as ((p: IntervalRenderer<unknown>) => ReactNode) | undefined,
        getRootStyleMemo(style),
        className,
        getLabelFormat,
        unit,
        headerData,
      ),
    [intervalRenderer, style, className, getLabelFormat, unit, headerData],
  );

  return (
    <CustomHeader
      children={CustomDateHeader as (p: unknown) => ReactNode}
      unit={headerUnit}
      height={height}
      headerData={headerDataValue}
    />
  );
}

export type DateHeaderWrapper<Data> = {
  unit?: keyof TimelineTimeSteps | 'primaryHeader';
  labelFormat?: typeof formatLabel;
  style?: CSSProperties;
  className?: string;
  intervalRenderer?: (props: IntervalRenderer<Data>) => ReactNode;
  headerData?: Data;
  height?: number;
};

export function DateHeader<Data>({
  labelFormat,
  unit,
  style,
  className,
  intervalRenderer,
  headerData,
  height,
}: DateHeaderWrapper<Data>) {
  return (
    <TimelineStateConsumer>
      {({ getTimelineState }) => {
        const timelineState = getTimelineState();
        return (
          <DateHeaderInner
            timelineUnit={timelineState.timelineUnit}
            unit={unit}
            labelFormat={labelFormat ?? formatLabel}
            style={style}
            className={className}
            intervalRenderer={intervalRenderer}
            headerData={headerData}
            height={height}
          />
        );
      }}
    </TimelineStateConsumer>
  );
}

function formatLabel(
  [timeStart]: [Dayjs, Dayjs],
  unit: keyof typeof defaultHeaderFormats,
  labelWidth: number,
  formatOptions = defaultHeaderFormats,
) {
  let format: string;
  if (labelWidth >= 150) {
    format = formatOptions[unit].long;
  } else if (labelWidth >= 100) {
    format = formatOptions[unit].mediumLong;
  } else if (labelWidth >= 50) {
    format = formatOptions[unit].medium;
  } else {
    format = formatOptions[unit].short;
  }
  return timeStart.format(format);
}

DateHeader.displayName = 'DateHeader';

export default DateHeader;
